import type { TFunction } from "i18next";

import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { TooltipHover } from "components/designV2/tooltip/tooltip";

import {
  SelectionActions,
  SelectionBanner,
  SelectionBannerCard,
  SelectionCount,
  SelectionHint,
  SelectionInfo,
  SelectionTotal,
} from "../personView.styled";
import type { BulkAction } from "../models/personView.actions";

interface ActionButtonConfig {
  action: BulkAction;
  kind: "primary" | "secondary";
  labelKey:
    | "TimeReports.Retract"
    | "TimeReports.Absence"
    | "General.Reset"
    | "CompanyTimeReport.Reject"
    | "CompanyTimeReport.Approve";
  iconName?: "v2/cross";
  styleOverrides?: Partial<BaseButtonStyle>;
  isLoading?: boolean;
}

interface PersonBulkActionsBarProps {
  readonly approveButtonStyles: Partial<BaseButtonStyle>;
  readonly availableBulkActions: readonly BulkAction[];
  readonly canRunActions: boolean;
  readonly denyButtonStyles: Partial<BaseButtonStyle>;
  readonly isBulkActionLoading: boolean;
  readonly readOnly: boolean;
  readonly restrictedActions?: readonly BulkAction[];
  readonly selectedCount: number;
  readonly totalTimeLabel: string;
  readonly onRunBulkAction: (action: BulkAction) => void;
  readonly translate: TFunction;
}

export const PersonBulkActionsBar = (
  props: PersonBulkActionsBarProps,
): JSX.Element | null => {
  const isActionDisabled = !props.canRunActions || props.isBulkActionLoading;
  const availableActions = new Set<BulkAction>(props.availableBulkActions);

  if (props.selectedCount === 0) return null;

  const actionButtons: ActionButtonConfig[] = [
    {
      action: "retract",
      kind: "secondary",
      labelKey: "TimeReports.Retract",
      isLoading: props.isBulkActionLoading,
    },
    {
      action: "absence",
      kind: "secondary",
      labelKey: "TimeReports.Absence",
      isLoading: false,
    },
    {
      action: "reset",
      kind: "secondary",
      labelKey: "General.Reset",
      isLoading: props.isBulkActionLoading,
    },
    {
      action: "reject",
      kind: "secondary",
      labelKey: "CompanyTimeReport.Reject",
      iconName: "v2/cross",
      styleOverrides: props.denyButtonStyles,
      isLoading: props.isBulkActionLoading,
    },
    {
      action: "approve",
      kind: "primary",
      labelKey: "CompanyTimeReport.Approve",
      styleOverrides: props.approveButtonStyles,
      isLoading: props.isBulkActionLoading,
    },
  ];

  return (
    <SelectionBanner>
      <SelectionBannerCard>
        <SelectionInfo>
          <SelectionCount>
            {props.translate("TimeReports.SelectedCount", {
              count: props.selectedCount,
            })}
          </SelectionCount>
          <SelectionTotal>{props.totalTimeLabel}</SelectionTotal>
        </SelectionInfo>
        <SelectionActions>
          {props.availableBulkActions.length === 0 && (
            <SelectionHint>
              {props.translate("TimeReports.NoCommonActions")}
            </SelectionHint>
          )}
          {actionButtons
            .filter((button) => availableActions.has(button.action))
            .map((button) => {
              const isRestricted =
                props.readOnly ||
                props.restrictedActions?.includes(button.action) === true;
              const actionButton =
                button.kind === "primary" ? (
                  <PrimaryButton
                    key={button.action}
                    type="button"
                    size="small"
                    label={props.translate(button.labelKey)}
                    styleOverrides={button.styleOverrides}
                    disabled={isActionDisabled || isRestricted}
                    isLoading={Boolean(button.isLoading)}
                    onClick={() => props.onRunBulkAction(button.action)}
                  />
                ) : (
                  <SecondaryButton
                    key={button.action}
                    type="button"
                    size="small"
                    label={props.translate(button.labelKey)}
                    iconName={button.iconName}
                    styleOverrides={button.styleOverrides}
                    disabled={isActionDisabled || isRestricted}
                    isLoading={Boolean(button.isLoading)}
                    onClick={() => props.onRunBulkAction(button.action)}
                  />
                );

              return isRestricted ? (
                <TooltipHover
                  key={button.action}
                  text={props.translate("Subscription.UpgradeToAccess")}
                >
                  {actionButton}
                </TooltipHover>
              ) : (
                actionButton
              );
            })}
        </SelectionActions>
      </SelectionBannerCard>
    </SelectionBanner>
  );
};
